import { useEffect } from 'react';
import { SUPPORT_BOT_URL } from '../ton/config';

export type LegalPageKind = 'terms' | 'privacy';

interface LegalSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

const termsSections: LegalSection[] = [
  {
    title: '1. Acceptance of these Terms',
    paragraphs: [
      'These Terms of Use govern access to pyramida.app, the Pyramida Telegram Mini App, related Telegram bots, and the interfaces provided by the Pyramida project. By using any of these services, you confirm that you have read and accepted these Terms.',
      'If you do not agree, do not connect a wallet, buy a ticket, swap tokens, provide liquidity, or otherwise use the services.',
    ],
  },
  {
    title: '2. Eligibility and lawful use',
    paragraphs: [
      'You must be at least 18 years old, have legal capacity to enter into these Terms, and be permitted to use blockchain, lottery, token swap, and liquidity services under the laws that apply to you.',
      'You are responsible for determining whether your use is lawful in your jurisdiction. Access may be restricted where required for legal, compliance, security, or technical reasons.',
    ],
  },
  {
    title: '3. Non-custodial service',
    paragraphs: [
      'Pyramida is a non-custodial interface. Wallet connection and transaction approval are handled through TonConnect and your chosen TON wallet. Pyramida does not hold your private keys, mnemonic, wallet password, or the ability to sign transactions for you.',
      'You are responsible for verifying every destination, token, amount, fee, minimum received amount, deadline, and payload shown by your wallet before approval. Blockchain transactions are generally irreversible.',
    ],
  },
  {
    title: '4. Lottery and referral functionality',
    paragraphs: [
      'Lottery tickets, round timing, prize distribution, PRMD emission, and referral rewards are executed or verified through smart contracts and public TON blockchain data. The deployed contract state is authoritative if the interface displays delayed or inconsistent information.',
      'Participation does not guarantee a prize or profit. Network fees, contract rules, minimum ticket amounts, timing, and eligibility conditions may apply. Referral rewards depend on valid on-chain activity and the contract rules in effect for that transaction.',
    ],
  },
  {
    title: '5. PRMD token',
    paragraphs: [
      'PRMD is a utility jetton used within the Pyramida ecosystem. It is not presented as a bank deposit, stablecoin, share, debt instrument, or promise of income.',
      'PRMD may be volatile, illiquid, unverified by third-party token lists, or unsupported by some wallets and exchanges. No price, liquidity, market availability, or future utility is guaranteed.',
    ],
  },
  {
    title: '6. Swaps and liquidity',
    paragraphs: [
      'Swap quotes and transaction routes may be supplied by third-party protocols such as Omniston and STON.fi. Liquidity operations interact with STON.fi contracts. Those protocols, pools, routers, wallets, and their terms are independent from Pyramida.',
    ],
    bullets: [
      'Quotes can expire or change before execution because of price movement, slippage, pool reserves, fees, and network conditions.',
      'Providing liquidity can result in impermanent loss, pool imbalance, reduced token value, smart-contract loss, or inability to withdraw at the expected price.',
      'A failed or partially executed transaction may still consume network or protocol fees.',
      'You must independently verify contract addresses and use amounts you can afford to lose.',
    ],
  },
  {
    title: '7. No financial, investment, or legal advice',
    paragraphs: [
      'Information shown by Pyramida is provided for product operation and general information only. It is not financial, investment, tax, accounting, or legal advice, and it is not a recommendation to buy, sell, hold, or provide liquidity for any asset.',
    ],
  },
  {
    title: '8. Risks',
    paragraphs: [
      'Use of TON, smart contracts, wallets, tokens, lotteries, swaps, liquidity pools, Telegram, and third-party APIs involves substantial risk. Risks include software defects, malicious contracts, phishing, compromised devices, loss of credentials, validator or network failure, incorrect metadata, price manipulation, regulatory changes, and permanent loss of assets.',
      'You accept these risks and remain solely responsible for your wallet and transaction decisions.',
    ],
  },
  {
    title: '9. Prohibited conduct',
    bullets: [
      'Using the services for fraud, money laundering, sanctions evasion, market manipulation, abuse, or any unlawful purpose.',
      'Attempting to bypass access controls, exploit vulnerabilities, disrupt infrastructure, impersonate another person, or submit misleading data.',
      'Using automated traffic in a way that degrades the services or violates third-party platform rules.',
    ],
  },
  {
    title: '10. Availability and changes',
    paragraphs: [
      'The services are provided on an “as is” and “as available” basis. Features may be changed, suspended, rate-limited, or discontinued without guaranteed notice. Maintenance, blockchain congestion, third-party outages, or security events may interrupt access.',
      'Interface updates do not alter already confirmed blockchain transactions. Smart contracts may have separate ownership, upgrade, or administrative rules visible on-chain.',
    ],
  },
  {
    title: '11. Intellectual property',
    paragraphs: [
      'Pyramida names, logos, interface designs, text, graphics, and original software are protected by applicable intellectual-property laws and licences. Third-party names, wallets, protocols, and trademarks belong to their respective owners.',
    ],
  },
  {
    title: '12. Disclaimer and limitation of liability',
    paragraphs: [
      'To the maximum extent permitted by law, Pyramida disclaims warranties of merchantability, fitness for a particular purpose, non-infringement, uninterrupted operation, accuracy, security, and compatibility.',
      'To the maximum extent permitted by law, Pyramida and its contributors are not liable for indirect, incidental, special, consequential, punitive, or blockchain-related losses, including lost assets, profits, data, opportunities, rewards, or access caused by your wallet, third-party services, smart contracts, network conditions, or use of the services. Mandatory rights that cannot legally be excluded remain unaffected.',
    ],
  },
  {
    title: '13. Changes and contact',
    paragraphs: [
      'We may update these Terms as the project, integrations, or legal requirements change. The revised version applies from the date published on this page. Continued use after publication constitutes acceptance of the updated Terms.',
      'Questions may be sent through the official Pyramida Telegram channel linked below.',
    ],
  },
];

const privacySections: LegalSection[] = [
  {
    title: '1. Scope',
    paragraphs: [
      'This Privacy Policy explains how the Pyramida project processes information when you use pyramida.app, the Pyramida Telegram Mini App, related Telegram bots, the partner dashboard, and connected TON features.',
      'Public blockchain records and data processed independently by Telegram, wallet providers, TON infrastructure, Omniston, STON.fi, Google, OpenRouter, or other third parties are also subject to their own policies.',
    ],
  },
  {
    title: '2. Information we process',
    bullets: [
      'Telegram data made available when you interact with a bot or Mini App, such as Telegram user ID, username, display name, language, chat details, and membership events.',
      'Public TON data, including wallet addresses, balances, transaction hashes, contract activity, referral keys, ticket purchases, rewards, PRMD activity, and liquidity positions.',
      'TonConnect proof data used to verify wallet ownership, including the signed proof, nonce, domain, timestamp, public key, and wallet address. We do not receive your private key or mnemonic.',
      'Referral and partner data, such as referral links, campaign and SubID labels, click identifiers, funnel events, postbacks, attribution records, and aggregate performance metrics.',
      'Messages, questionnaire answers, support requests, and AI-bot conversations that you choose to send.',
      'Technical and security data, such as IP address, browser and device information, request timestamps, error logs, rate-limit events, and suspected abuse indicators.',
      'Local preferences and session data, including language, attribution, authentication, and interface state stored through cookies, local storage, or similar browser mechanisms.',
    ],
  },
  {
    title: '3. Why we use information',
    bullets: [
      'To operate the lottery, token, swap, liquidity, referral, notification, partner, and support features you request.',
      'To authenticate sessions, verify wallet ownership, associate eligible Telegram and wallet activity, and prevent unauthorized payouts.',
      'To calculate statistics, referrals, funnel events, rewards, and service performance.',
      'To deliver transactional or requested Telegram notifications and respond to messages.',
      'To protect users and infrastructure, prevent fraud and abuse, debug errors, and comply with legal obligations.',
      'To improve product reliability and understand aggregate feature usage.',
    ],
  },
  {
    title: '4. Legal grounds',
    paragraphs: [
      'Depending on your location and the feature used, processing may be necessary to provide a service you requested, based on your consent, required for compliance with law, or supported by legitimate interests such as security, fraud prevention, service reliability, and product improvement.',
      'You may disconnect your wallet, stop using the bots, revoke optional Telegram permissions, or clear local browser data at any time. Some processing is necessary for requested transactions or account security.',
    ],
  },
  {
    title: '5. Sharing and service providers',
    paragraphs: [
      'We do not sell personal data. Information is shared only as needed to operate the services, comply with law, protect users, or complete actions you request.',
    ],
    bullets: [
      'Telegram receives and delivers bot and Mini App communications.',
      'TON, TonConnect wallets, blockchain RPC/indexing providers, Omniston, and STON.fi process wallet requests, quotes, public transactions, or pool data.',
      'Google Cloud hosts application, database, logging, queue, and deployment infrastructure.',
      'OpenRouter and selected model providers may process messages sent to AI-powered bot features.',
      'Professional advisers, authorities, or security providers may receive limited information where legally required or reasonably necessary to protect rights and systems.',
    ],
  },
  {
    title: '6. Public blockchain data',
    paragraphs: [
      'TON transactions are public and may permanently expose wallet addresses, amounts, tokens, contract calls, and related activity. Pyramida cannot edit or erase information recorded on TON or independently retained by blockchain participants and indexers.',
      'Avoid placing personal or confidential information in transaction payloads, wallet comments, public referral labels, or other on-chain fields.',
    ],
  },
  {
    title: '7. Retention',
    paragraphs: [
      'We retain information for as long as needed to provide the relevant feature, maintain transaction and referral integrity, prevent fraud, resolve incidents, comply with legal obligations, and establish or defend claims. Retention periods vary by data type and operational need.',
      'Local browser data remains until it expires or you clear it. Telegram and third-party providers apply their own retention rules. Public blockchain records are effectively permanent.',
    ],
  },
  {
    title: '8. Security',
    paragraphs: [
      'We use technical and organisational safeguards such as access controls, signed sessions, wallet proof verification, restricted service credentials, transport encryption, logging, and delivery deduplication. No internet, cloud, Telegram, wallet, or blockchain system can be guaranteed completely secure.',
      'Never send a private key, mnemonic, wallet password, or recovery phrase to Pyramida or anyone claiming to represent Pyramida.',
    ],
  },
  {
    title: '9. International processing',
    paragraphs: [
      'The services and providers operate across multiple countries. Information may be processed outside your country under the safeguards available to the relevant provider and as permitted by applicable law.',
    ],
  },
  {
    title: '10. Your choices and rights',
    paragraphs: [
      'Depending on your jurisdiction, you may have rights to request access, correction, deletion, restriction, objection, portability, or withdrawal of consent. These rights may be limited where information is public on-chain, needed for security or legal records, or controlled by an independent third party.',
      'To make a privacy request, contact Pyramida through the official Telegram link below and provide enough information to identify the relevant account or record. We may need to verify that you control the Telegram account or wallet concerned.',
    ],
  },
  {
    title: '11. Children',
    paragraphs: [
      'The services are not intended for persons under 18. We do not knowingly offer lottery, token swap, or liquidity functionality to children.',
    ],
  },
  {
    title: '12. Policy updates',
    paragraphs: [
      'We may update this Policy when features, providers, security practices, or legal requirements change. The current version and effective date are published on this page.',
    ],
  },
];

const content = {
  terms: {
    title: 'Pyramida Terms of Use',
    intro:
      'Rules and risk disclosures for the Pyramida website, Telegram experiences, TON lottery, PRMD, swaps, and liquidity tools.',
    sections: termsSections,
  },
  privacy: {
    title: 'Pyramida Privacy Policy',
    intro:
      'How Pyramida processes Telegram, wallet, blockchain, referral, partner, and technical data.',
    sections: privacySections,
  },
} satisfies Record<
  LegalPageKind,
  { title: string; intro: string; sections: LegalSection[] }
>;

interface LegalPageProps {
  kind: LegalPageKind;
}

export function LegalPage({ kind }: LegalPageProps) {
  const page = content[kind];

  useEffect(() => {
    document.title = page.title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', page.intro);
  }, [page]);

  return (
    <div className="legal-page">
      <header className="legal-page__header">
        <a href="/" className="legal-page__brand" aria-label="Pyramida home">
          <img src="/text-logo.webp" alt="Pyramida" />
        </a>
        <nav className="legal-page__nav" aria-label="Legal documents">
          <a className={kind === 'terms' ? 'active' : undefined} href="/terms">
            Terms
          </a>
          <a className={kind === 'privacy' ? 'active' : undefined} href="/privacy">
            Privacy
          </a>
        </nav>
      </header>

      <main className="legal-page__main">
        <article className="legal-document">
          <p className="legal-document__eyebrow">Effective 14 July 2026</p>
          <h1>{page.title}</h1>
          <p className="legal-document__intro">{page.intro}</p>

          {page.sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {section.bullets && (
                <ul>
                  {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                </ul>
              )}
            </section>
          ))}

          <footer className="legal-document__footer">
            <span>Official project contact:</span>
            <a href="https://t.me/pyramida" target="_blank" rel="noreferrer">
              t.me/pyramida
            </a>
            <span aria-hidden="true">·</span>
            <a href={SUPPORT_BOT_URL} target="_blank" rel="noreferrer">
              t.me/prmd_support_bot
            </a>
            <span aria-hidden="true">·</span>
            <a href="/">Return to Pyramida</a>
          </footer>
        </article>
      </main>
    </div>
  );
}
